import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MediaService, MEDIA_CATEGORIES } from '../../services/media/media.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { MediaUploadDialogComponent } from './media-upload-dialog/media-upload-dialog.component';
import { MediaEditDialogComponent } from './media-edit-dialog/media-edit-dialog.component';

// Configurations > Media: images shared centrally or per establishment. Upload /
// edit / activate need can_manage; the utsav forms pick from this library.
@Component({
  selector: 'app-media-library',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './media-library.component.html',
  styleUrl: './media-library.component.scss',
})
export class MediaLibraryComponent implements OnInit {
  media: any[] = [];
  isLoading: boolean = false;
  canManage: boolean = false;
  // what the backend says this user may upload (drives the upload dialog)
  reach: { canCentral: boolean; canAllEstablishments: boolean; establishmentIds: number[] } = {
    canCentral: false,
    canAllEstablishments: false,
    establishmentIds: [],
  };

  readonly categories = MEDIA_CATEGORIES;
  scope = new FormControl<string>('');
  category = new FormControl<string>('');
  status = new FormControl<string>('1');
  search = new FormControl('');

  constructor(
    private mediaService: MediaService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canManage =
      localStorage.getItem('isSuperAdmin') === 'true' || this.permissionService.can('media', 'can_manage');
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.mediaService
      .listMedia({
        scope: this.scope.value || undefined,
        category: this.category.value || undefined,
        status: this.status.value || undefined,
      })
      .subscribe({
        next: (res: any) => {
          this.media = res?.data?.media || [];
          this.reach = {
            canCentral: !!res?.data?.canCentral,
            canAllEstablishments: !!res?.data?.canAllEstablishments,
            establishmentIds: res?.data?.establishmentIds || [],
          };
          this.isLoading = false;
        },
        error: (err) => {
          this.media = [];
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to load media.');
        },
      });
  }

  filteredMedia(): any[] {
    const term = (this.search.value || '').trim().toLowerCase();
    if (!term) return this.media;
    return this.media.filter((m) =>
      `${m.name} ${m.category} ${m.establishmentName || ''} ${m.remarks || ''}`.toLowerCase().includes(term)
    );
  }

  placeOf(item: any): string {
    return item.scope === 'CENTRAL' ? 'Central' : item.establishmentName || `Establishment #${item.establishmentId}`;
  }

  upload(): void {
    if (!this.canManage) return;
    const ref = this.dialog.open(MediaUploadDialogComponent, {
      width: '640px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 32px)',
      data: { reach: this.reach },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  edit(item: any): void {
    if (!this.canManage) return;
    const ref = this.dialog.open(MediaEditDialogComponent, {
      width: '560px',
      maxWidth: 'calc(100vw - 32px)',
      data: { media: item },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  toggleStatus(item: any): void {
    if (!this.canManage) return;
    this.isLoading = true;
    this.mediaService.setMediaStatus(item.mediaId, !item.isActive).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Media updated.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to update media.');
      },
    });
  }

  reset(): void {
    this.scope.setValue('');
    this.category.setValue('');
    this.status.setValue('1');
    this.search.setValue('');
    this.load();
  }
}
